import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { ArrowRight, Users, Building2, FlaskConical } from "lucide-react";
import { Kicker, Reveal } from "../motion";

const MAP = "https://static.prod-images.emergentagent.com/jobs/60fe7cfd-579c-48af-b4a9-bdfd3f1b809e/images/bf5b5136019b4b043d8fe5991bb61df5a36b61cef5a03cbb3e1c83f7247ce98a.jpeg";
const NODES = [[22, 38], [35, 30], [48, 62], [58, 46], [66, 34], [74, 58], [82, 40]];

export default function NetworkMap() {
  const { t } = useTranslation();
  const rows = [
    { Icon: Users, l: t("home.network.card_l1"), v: t("home.network.card_v1") },
    { Icon: Building2, l: t("home.network.card_l2"), v: t("home.network.card_v2") },
    { Icon: FlaskConical, l: t("home.network.card_l3"), v: t("home.network.card_v3") },
  ];

  return (
    <section className="relative surface-1 py-20 lg:py-28 overflow-hidden" data-testid="home-network">
      <div className="max-w-[1500px] mx-auto px-6 lg:px-12">
        <div className="paper !bg-[#F1F1E7] p-6 lg:p-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center overflow-hidden">
          <Reveal className="lg:col-span-3">
            <Kicker>{t("home.network.kicker")}</Kicker>
            <h2 className="font-display text-4xl lg:text-5xl mt-4 leading-[1.05] text-ink">{t("home.network.title")}</h2>
            <p className="mt-5 text-slate leading-relaxed">{t("home.network.body")}</p>
            <Link to="/about" data-testid="home-network-cta" className="btn-forest mt-8 !py-2.5 !px-5 !text-[11px]">
              {t("home.network.cta")} <ArrowRight className="h-3.5 w-3.5 rtl:rotate-180" />
            </Link>
          </Reveal>

          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-6 relative"
          >
            <img src={MAP} alt="Arab world research network" className="w-full h-auto select-none" />
            {NODES.map(([x, y], i) => (
              <span key={i} className="absolute h-3 w-3 -ml-1.5 -mt-1.5 pointer-events-none" style={{ left: `${x}%`, top: `${y}%` }}>
                <span className="absolute inset-0 rounded-full bg-gold-light" style={{ animation: `pulse-node 2.8s ease-out ${i * 0.4}s infinite` }} />
              </span>
            ))}
          </motion.div>

          <Reveal delay={0.15} className="lg:col-span-3">
            <div className="paper p-6" data-testid="home-network-card">
              <div className="font-display text-2xl text-ink">{t("home.network.card_title")}</div>
              <ul className="mt-5 divide-y divide-line">
                {rows.map(({ Icon, l, v }) => (
                  <li key={l} className="flex items-center justify-between gap-4 py-3">
                    <span className="flex items-center gap-3 text-sm text-slate"><Icon className="h-4 w-4 text-gold" />{l}</span>
                    <span className="font-display text-xl font-bold text-forest">{v}</span>
                  </li>
                ))}
              </ul>
              <Link to="/about" className="mt-5 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] text-forest gxr-link">
                {t("home.network.card_cta")} <ArrowRight className="h-3.5 w-3.5 rtl:rotate-180" />
              </Link>
              <div className="mt-4 text-xs text-slate italic">{t("home.network.card_note")}</div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
